import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { User, Car, X } from "@phosphor-icons/react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const pickRole = (role) => {
    update("role", role);
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await register(form);
      if (u.role === "driver") navigate("/driver");
      else navigate("/passenger");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "#2B2B2B" }}>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 fade-in" data-testid="register-modal">
        {!showForm ? (
          <>
            <div className="flex items-start justify-between mb-2">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">¿Cuál es tu rol?</h1>
                <p className="text-slate-500 text-sm mt-2">Selecciona si eres cliente o conductor para acceder a tu portal</p>
              </div>
              <Link to="/" className="text-slate-400 hover:text-slate-700" data-testid="close-register-btn">
                <X size={22} />
              </Link>
            </div>

            <div className="border-t border-slate-100 my-6" />

            <button
              type="button"
              onClick={() => pickRole("passenger")}
              className="w-full bg-white border border-slate-200 hover:border-amber-300 hover:shadow-md rounded-2xl p-6 mb-4 transition text-left"
              data-testid="role-passenger-btn"
            >
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4" style={{ background: "#FFF4CC" }}>
                <User size={28} weight="regular" style={{ color: "#F5B700" }} />
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-slate-900">Soy Cliente</div>
                <div className="text-sm text-slate-500 mt-1">Solicitar y rastrear taxis</div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => pickRole("driver")}
              className="w-full bg-white border border-slate-200 hover:border-amber-300 hover:shadow-md rounded-2xl p-6 transition text-left"
              data-testid="role-driver-btn"
            >
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4" style={{ background: "#FFF4CC" }}>
                <Car size={28} weight="regular" style={{ color: "#F5B700" }} />
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-slate-900">Soy Conductor</div>
                <div className="text-sm text-slate-500 mt-1">Gestionar solicitudes de viaje</div>
              </div>
            </button>

            <div className="text-center mt-6 text-sm text-slate-600">
              ¿Ya tienes cuenta?{" "}
              <Link to="/login" className="font-bold" style={{ color: "#F5B700" }} data-testid="goto-login-link">
                Inicia sesión
              </Link>
            </div>
          </>
        ) : (
          <form onSubmit={submit} data-testid="register-form">
            <div className="flex items-start justify-between mb-4">
              <div>
                <button type="button" onClick={() => setShowForm(false)} className="text-sm text-slate-500 hover:text-slate-900 mb-2" data-testid="back-to-role-btn">
                  ← Cambiar rol
                </button>
                <h1 className="text-2xl font-bold text-slate-900">
                  {form.role === "driver" ? "Crear cuenta · Conductor" : "Crear cuenta · Cliente"}
                </h1>
              </div>
              <Link to="/" className="text-slate-400 hover:text-slate-700"><X size={22} /></Link>
            </div>

            <div className="border-t border-slate-100 my-5" />

            <div className="space-y-4">
              <div>
                <label className="label">Nombre completo</label>
                <input className="input-field" required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="reg-name-input" />
              </div>
              <div>
                <label className="label">Email</label>
                <input className="input-field" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="reg-email-input" />
              </div>
              <div>
                <label className="label">Teléfono</label>
                <input className="input-field" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="reg-phone-input" />
              </div>
              <div>
                <label className="label">Contraseña (mín. 6)</label>
                <input className="input-field" type="password" required minLength={6} value={form.password} onChange={(e) => update("password", e.target.value)} data-testid="reg-password-input" />
              </div>
            </div>

            {error && <div className="mt-4 p-3 rounded-xl bg-red-50 text-red-700 text-sm" data-testid="register-error">{error}</div>}

            <button type="submit" disabled={loading} className="btn-primary w-full mt-6" data-testid="register-submit-btn">
              {loading ? "Creando..." : "Crear cuenta"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
